import React from "react";
import { motion } from "framer-motion";
import {
  Star,
  Wifi,
  FileText,
  Dumbbell,
  MessageCircle,
  Home,
  Eye,
  Car,
  Sparkles,
  UtensilsCrossed,
  PawPrint,
  Bath,
} from "lucide-react";

export default function LivingConditionsSection({ hotel }) {
  if (!hotel) return null;

  const hasData = (field) => {
    const value = hotel[field];
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === "string") return value.length > 0;
    return false;
  };

  const serviceCategories = [
    {
      key: "popular_services",
      icon: Star,
      title: hotel.options?.popular_services || "Преимущества",
      color: "text-orange-500",
      priority: true,
    },
    {
      key: "hotels_internet",
      icon: Wifi,
      title: hotel.options?.hotels_internet || "Интернет",
      color: "text-gray-700",
    },
    {
      key: "hotels_business",
      icon: FileText,
      title: hotel.options?.hotels_business || "Бизнес",
      color: "text-gray-700",
    },
    {
      key: "hotels_near_sport",
      icon: Dumbbell,
      title: hotel.options?.hotels_near_sport || "Спорт",
      color: "text-gray-700",
    },
    {
      key: "staff_speaking",
      icon: MessageCircle,
      title: hotel.options?.staff_speaking || "Персонал говорит",
      color: "text-gray-700",
    },
    {
      key: "hotels_overall",
      icon: Home,
      title: hotel.options?.hotels_overall || "В номерах",
      color: "text-gray-700",
    },
    {
      key: "hotels_availability",
      icon: Eye,
      title: hotel.options?.hotels_availability || "Доступность",
      color: "text-gray-700",
    },
    {
      key: "hotels_parking",
      icon: Car,
      title: hotel.options?.hotels_parking || "Парковка",
      color: "text-gray-700",
    },
    {
      key: "hotels_near_beatuy",
      icon: Sparkles,
      title: hotel.options?.hotels_near_beatuy || "Красота и здоровье",
      color: "text-gray-700",
    },
    {
      key: "hotels_meals",
      icon: UtensilsCrossed,
      title: hotel.options?.hotels_meals || "Питание",
      color: "text-gray-700",
    },
    {
      key: "hotels_pet",
      icon: PawPrint,
      title: hotel.options?.hotels_pet || "Животные",
      color: "text-gray-700",
    },
    {
      key: "hotels_cleaning",
      icon: Sparkles,
      title: hotel.options?.hotels_cleaning || "Санитарные меры",
      color: "text-gray-700",
    },
    {
      key: "hotels_common_area",
      icon: Home,
      title: hotel.options?.hotels_common_area || "Общее",
      color: "text-gray-700",
    },
    {
      key: "hotels_wc_additional",
      icon: Bath,
      title: hotel.options?.hotels_wc_additional || "Услуги и удобства",
      color: "text-gray-700",
    },
  ];

  let availableCategories = serviceCategories.filter((category) =>
    hasData(category.key),
  );

  if (availableCategories.length === 0) return null;

  // ✅ Преимущества всегда в начале списка
  const popularIndex = availableCategories.findIndex(
    (c) => c.key === "popular_services",
  );
  if (popularIndex > 0) {
    const [popular] = availableCategories.splice(popularIndex, 1);
    availableCategories = [popular, ...availableCategories];
  }

  const getCategoryItems = (key) => {
    const value = hotel[key];
    if (Array.isArray(value)) return value;
    return [value];
  };

  // ✅ Равномерное распределение по колонкам (последняя может быть меньше)
  const distributeToColumns = (items, columnsCount) => {
    if (items.length === 0) return [];

    // Разделяем: преимущества всегда в первой колонке
    const hasPriority = items[0]?.key === "popular_services";
    const priorityItem = hasPriority ? items[0] : null;
    const otherItems = hasPriority ? items.slice(1) : items;

    // Рассчитываем высоту для каждого элемента
    const itemsWithHeight = otherItems.map((item) => ({
      ...item,
      height: getCategoryItems(item.key).length,
    }));

    // Сортируем по убыванию высоты для лучшего распределения
    const sorted = [...itemsWithHeight].sort((a, b) => b.height - a.height);

    // Создаем колонки
    let columns = Array(columnsCount)
      .fill()
      .map(() => ({
        items: [],
        totalHeight: 0,
      }));

    // Если есть приоритетный элемент, добавляем его в первую колонку
    if (priorityItem) {
      columns[0].items.push(priorityItem);
      columns[0].totalHeight = getCategoryItems(priorityItem.key).length;
    }

    // Распределяем остальные элементы (начиная с первой колонки, если нет приоритетного)
    let startCol = priorityItem ? 0 : 0;

    sorted.forEach((item) => {
      // Ищем колонку с наименьшей высотой
      let minIndex = startCol;
      for (let i = startCol; i < columnsCount; i++) {
        if (columns[i].totalHeight < columns[minIndex].totalHeight) {
          minIndex = i;
        }
      }
      columns[minIndex].items.push(item);
      columns[minIndex].totalHeight += item.height;
    });

    // Финальное выравнивание: перебрасываем элементы для баланса
    let improved = true;
    let attempts = 0;
    while (improved && attempts < 50) {
      improved = false;
      attempts++;

      let maxCol = 0,
        minCol = 0;
      for (let i = 0; i < columnsCount; i++) {
        if (columns[i].totalHeight > columns[maxCol].totalHeight) maxCol = i;
        if (columns[i].totalHeight < columns[minCol].totalHeight) minCol = i;
      }

      const diff = columns[maxCol].totalHeight - columns[minCol].totalHeight;
      if (diff <= 2) break;

      // Не перемещаем приоритетный элемент из первой колонки
      if (
        maxCol === 0 &&
        columns[0].items[0]?.key === "popular_services" &&
        columns[0].items.length > 1
      ) {
        // Перемещаем второй элемент
        const [first, second, ...rest] = columns[maxCol].items;
        if (second) {
          columns[maxCol].items = [first, ...rest];
          columns[maxCol].totalHeight -= second.height;
          columns[minCol].items.push(second);
          columns[minCol].totalHeight += second.height;
          improved = true;
        }
      } else if (
        maxCol !== 0 ||
        columns[0].items[0]?.key !== "popular_services"
      ) {
        const lastItem = columns[maxCol].items.pop();
        if (lastItem) {
          columns[maxCol].totalHeight -= lastItem.height;
          columns[minCol].items.push(lastItem);
          columns[minCol].totalHeight += lastItem.height;
          improved = true;
        }
      }
    }

    return columns;
  };

  const columnsForMobile = distributeToColumns(availableCategories, 1);
  const columnsForTablet = distributeToColumns(availableCategories, 2);
  const columnsForDesktop = distributeToColumns(availableCategories, 4);

  const renderColumn = (column, colIndex) => (
    <div key={colIndex} className="space-y-6">
      {column.items.map((category, idx) => (
        <motion.div
          key={category.key}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: colIndex * 0.1 + idx * 0.03 }}
        >
          <div className="flex items-center gap-2 mb-4">
            <category.icon className={`w-5 h-5 ${category.color}`} />
            <h3 className="font-semibold text-gray-900">{category.title}</h3>
          </div>
          <ul className="space-y-2 text-sm text-gray-600">
            {getCategoryItems(category.key).map((item, itemIdx) => (
              <li key={itemIdx} className="flex items-start gap-2">
                <span className="inline-block mt-1">•</span>
                <span className="flex-1">{item}</span>
              </li>
            ))}
          </ul>
        </motion.div>
      ))}
    </div>
  );

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-12"
        >
          <h2 className="text-3xl font-bold text-gray-900">
            Услуги и удобства
          </h2>
        </motion.div>

        {/* Мобильные */}
        <div className="block md:hidden">
          <div className="space-y-6">
            {columnsForMobile[0]?.items.map((category, idx) => (
              <motion.div
                key={category.key}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.05 }}
              >
                <div className="flex items-center gap-2 mb-4">
                  <category.icon className={`w-5 h-5 ${category.color}`} />
                  <h3 className="font-semibold text-gray-900">
                    {category.title}
                  </h3>
                </div>
                <ul className="space-y-2 text-sm text-gray-600">
                  {getCategoryItems(category.key).map((item, itemIdx) => (
                    <li key={itemIdx} className="flex items-start gap-2">
                      <span className="inline-block mt-1">•</span>
                      <span className="flex-1">{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Планшеты */}
        <div className="hidden md:block lg:hidden">
          <div className="grid grid-cols-2 gap-6">
            {columnsForTablet.map((col, idx) => renderColumn(col, idx))}
          </div>
        </div>

        {/* Десктоп */}
        <div className="hidden lg:block">
          <div className="grid grid-cols-4 gap-6">
            {columnsForDesktop.map((col, idx) => renderColumn(col, idx))}
          </div>
        </div>
      </div>
    </section>
  );
}
